import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import CTAPropTypes from '../../prop-types/cta.pim';

import * as Styled from './ImageCard.styled';

import { ImageCardFragment } from './ImageCard.gql';
import ImageProps from '../../prop-types/image-props.pim';

/**
 * A image with text and a CTA overlayed.
 *
 * Component is consumed by the Content System
 */
const ImageCard = ({
  ctas,
  header,
  gridSpan,
  image,
  imageCardTextColor: textColor,
  subheader,
  textAlignment,
  responsiveStyleSize
}) => {
  const [areCtasRevealed, setAreCtasRevealed] = useState(false);
  const handleGlobalClick = () => setAreCtasRevealed(false);
  const src = image?.file.url;
  const height = image?.file && image.file.details.image.height;
  const width = image?.file && image.file.details.image.width;

  useEffect(() => {
    if (areCtasRevealed) {
      document.addEventListener('click', handleGlobalClick, {
        capture: true,
        once: true
      });
    }

    return () => {
      document.removeEventListener('click', handleGlobalClick, {
        capture: true
      });
    };
  }, [areCtasRevealed]);

  const styledImage = (
    <Styled.Image
      src={src.match(/https|http/gi) ? src : `https://${src}`}
      alt={image?.description || ''}
      resolution={{
        small: 270,
        medium: 540,
        large: 720
      }}
      crop={{
        height,
        width
      }}
      $isClickable={ctas?.length > 0}
      $areCtasRevealed={areCtasRevealed}
    />
  );

  return (
    <Styled.ImageCard
      $alignment={textAlignment}
      $gridSpan={gridSpan}
      onClick={() => ctas?.length > 1 && setAreCtasRevealed(true)}
      $areCtasRevealed={areCtasRevealed}
    >
      {ctas?.length === 1 ? (
        <Styled.Link
          href={ctas?.length && ctas[0].url}
          aria-label={image?.description || header || 'Bombas'}
        >
          {styledImage}
        </Styled.Link>
      ) : (
        styledImage
      )}
      <Styled.Copy
        $responsiveStyleSize={responsiveStyleSize}
        $areCtasRevealed={areCtasRevealed}
      >
        <Styled.Title
          $responsiveStyleSize={responsiveStyleSize}
          $textColor={textColor}
        >
          {header}
        </Styled.Title>
        {!!subheader && Object.keys(subheader).length > 0 && (
          <Styled.Subtitle
            $textColor={textColor}
            $responsiveStyleSize={responsiveStyleSize}
          >
            {documentToReactComponents(subheader)}
          </Styled.Subtitle>
        )}
        {ctas?.length === 1 &&
          !!(ctas?.length && ctas[0].text && ctas[0].text.trim()) && (
            <Styled.Cta
              $textColor={textColor}
              as="a"
              href={ctas?.length && ctas[0].url}
              size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
            >
              {ctas?.length && ctas[0].text}
            </Styled.Cta>
          )}

        {ctas?.length > 1 && !!(ctas[0].text && ctas[0].text.trim()) && (
          <Styled.Cta
            $textColor={textColor}
            href={ctas?.length && ctas[0].url}
            onClick={() => setAreCtasRevealed(true)}
            size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
          >
            {ctas?.length && ctas[0].text}
          </Styled.Cta>
        )}
      </Styled.Copy>
      {ctas?.length > 1 && areCtasRevealed && (
        <Styled.CtasContainer>
          <Styled.CtasBackdrop $areCtasRevealed={areCtasRevealed} />
          <Styled.Ctas $responsiveStyleSize={responsiveStyleSize}>
            {ctas.slice(1).map(
              ({ text, url }) =>
                text &&
                text.trim() && (
                  <Styled.Cta
                    as="a"
                    href={url}
                    key={header + text + url}
                    size={responsiveStyleSize === 'mega' ? 'large' : 'medium'}
                    $textColor={textColor}
                  >
                    {text}
                  </Styled.Cta>
                )
            )}
          </Styled.Ctas>
        </Styled.CtasContainer>
      )}
    </Styled.ImageCard>
  );
};

ImageCard.propTypes = {
  /**
   * An image object.
   */
  image: ImageProps,
  /**
   * The header of the image card
   */
  header: PropTypes.string,
  /**
   * The supporting text for the header.
   *
   * Renders as contentful rich text
   */
  subheader: PropTypes.shape({}),
  /**
   * Text color can be light or dark
   *
   * @default 'LIGHT'
   */
  imageCardTextColor: PropTypes.oneOf(['LIGHT', 'DARK']),
  /**
   * An array of CTAs
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CTAPropTypes)),
  /**
   * Column widths in our 12 column grid. These values correspond to values derived from the content system design specs-
   *
   * 16.6%, 20%, 25%, 33.3%, 50%
   *
   * When a 20% grid is used, CardContainer will use a custom 10 column grid
   *
   * @default 6
   */
  gridSpan: PropTypes.oneOf([2, 3, 4, 5, 6, 12]),
  /**
   * Alignment of the copy
   *
   * @default 'left'
   */
  textAlignment: PropTypes.oneOf(['LEFT', 'CENTER']),
  /*
   * Sets the styling of the copy
   *
   * @default 'small'
   */
  responsiveStyleSize: PropTypes.oneOf(['mini', 'regular', 'mega'])
};

ImageCard.defaultProps = {
  image: {},
  header: '',
  subheader: {},
  imageCardTextColor: 'LIGHT',
  ctas: [],
  gridSpan: 6,
  textAlignment: 'LEFT',
  responsiveStyleSize: 'regular'
};

ImageCard.fragment = ImageCardFragment;

export default ImageCard;
